import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Breadcrumb Store
 *
 * Complete breadcrumb management using Zustand.
 * Handles navigation history, entity name caching, and breadcrumb segment generation.
 * Pages should register entities when they load data.
 *
 * Redux DevTools enabled in development for debugging.
 */

export interface BreadcrumbSegment {
  label: string;
  path: string;
  isClickable: boolean;
}

export interface NavigationHistoryEntry {
  path: string;
  label: string;
  timestamp: number;
}

export interface BreadcrumbConfig {
  [key: string]: {
    alias?: string;
    hidden?: boolean;
    clickable?: boolean;
    path?: string;
    isRootSection?: boolean;
    /**
     * Keep this root section's label in the trail even when the current path is a
     * direct child (e.g. `/root/:id`). By default a direct child collapses the root
     * label (the Back button already returns there); set this to opt out and always
     * render the full `Root › {child}` trail.
     */
    keepRootInChildTrail?: boolean;
  };
}

// Default breadcrumb configuration
const defaultBreadcrumbConfig: BreadcrumbConfig = {
  '/': {
    alias: 'Home',
    clickable: true,
  },
  '/admin': {
    alias: 'Users & Permissions',
    clickable: true,
    path: '/admin',
    isRootSection: true,
  },
  '/dashboard': {
    alias: 'Dashboard',
    clickable: true,
    path: '/dashboard',
    isRootSection: true,
  },
  '/clients': {
    alias: 'Clients',
    clickable: true,
    path: '/clients',
    isRootSection: true,
  },
  '/ai-agent-hub': {
    alias: 'AI Agent Hub',
    clickable: true,
    path: '/ai-agent-hub',
    isRootSection: true,
    // Keep the section label in the trail on a direct child (`/ai-agent-hub/:agentKey`)
    // so it reads "AI Agent Hub › {child}" rather than collapsing to just the child.
    keepRootInChildTrail: true,
  },
  '/invitations': {
    alias: 'Users',
    clickable: false,
  },
  '/projects': {
    alias: 'Projects',
    clickable: true,
  },
  '/steering-document': {
    alias: 'Steering Documents',
    clickable: false,
  },
  '/library': {
    alias: 'Library',
    clickable: false,
  },
  '/team-members': {
    alias: 'Team Members',
    clickable: false,
  },
  '/documents': {
    hidden: true,
  },
  '/prd': {
    alias: 'Document PRD',
    clickable: false,
  },
  '/brief': {
    alias: 'Document Brief',
    clickable: false,
  },
  '/architecture': {
    alias: 'Document Architecture',
    clickable: false,
  },
  '/workspace': {
    hidden: true,
  },
  '/kanban': {
    alias: 'Kanban',
    clickable: false,
  },
  '/milestones': {
    alias: 'Milestones',
    clickable: false,
  },
};

interface BreadcrumbStore {
  // Entity name cache (generic - works for any entity type)
  entities: Record<string, string>;
  // Legacy caches (kept for backward compatibility)
  tenants: Record<string, string>;
  projects: Record<string, string>;

  // Navigation state
  currentPath: string;
  history: NavigationHistoryEntry[];
  segments: BreadcrumbSegment[];

  // Configuration
  config: BreadcrumbConfig;
  maxHistorySize: number;

  // Actions
  registerEntity: (entityId: string, entityName: string) => void;
  getEntityName: (entityId: string) => string | undefined;
  // Legacy actions (kept for backward compatibility, delegate to generic)
  registerTenant: (tenantId: string, tenantName: string) => void;
  registerProject: (projectId: string, projectName: string) => void;
  getTenantName: (tenantId: string) => string | undefined;
  getProjectName: (projectId: string) => string | undefined;
  setCurrentPath: (path: string) => void;
  setConfig: (config: BreadcrumbConfig) => void;
  clearHistory: () => void;
  clear: () => void;
}

// Helper functions
const getBreadcrumbConfig = (segment: string, config: BreadcrumbConfig) => {
  const exactMatch = config[segment];
  if (exactMatch) return exactMatch;

  const withSlash = config[`/${segment}`];
  if (withSlash) return withSlash;

  return null;
};

const isHidden = (segment: string, config: BreadcrumbConfig): boolean => {
  const segmentConfig = getBreadcrumbConfig(segment, config);
  return segmentConfig?.hidden === true;
};

const generateLabel = (segment: string, config: BreadcrumbConfig): string => {
  const segmentConfig = getBreadcrumbConfig(segment, config);
  if (segmentConfig?.alias) {
    return segmentConfig.alias;
  }
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const isClickable = (
  segment: string,
  isCurrentPage: boolean,
  config: BreadcrumbConfig
): boolean => {
  if (isCurrentPage) return false;
  const segmentConfig = getBreadcrumbConfig(segment, config);
  if (segmentConfig?.clickable !== undefined) {
    return segmentConfig.clickable;
  }
  return true;
};

const isSegmentResolved = (
  segment: string,
  config: BreadcrumbConfig,
  entities: Record<string, string>
): boolean => {
  // Segment is resolved if it has a config alias OR is in entity cache
  const segmentConfig = getBreadcrumbConfig(segment, config);
  if (segmentConfig?.alias) return true;
  return !!entities[segment];
};

const buildSegments = (
  currentPath: string,
  history: NavigationHistoryEntry[],
  config: BreadcrumbConfig,
  entities: Record<string, string>
): BreadcrumbSegment[] => {
  if (!currentPath || currentPath === '/') {
    return [];
  }

  const pathSegments = currentPath.split('/').filter(Boolean);
  const segmentList: BreadcrumbSegment[] = [];
  let pathSoFar = '';

  // Check if current page is a root section
  const isCurrentPageRootSection = pathSegments.some((segment) => {
    if (isHidden(segment, config)) return false;
    const segmentConfig = getBreadcrumbConfig(segment, config);
    if (segmentConfig?.isRootSection && segmentConfig?.path) {
      return segmentConfig.path === currentPath;
    }
    return false;
  });

  // Find root segment from history
  let rootEntry = null;
  let skippedRootSegment: string | null = null;
  if (history.length >= 2 && !isCurrentPageRootSection) {
    for (let i = history.length - 2; i >= 0; i -= 1) {
      const entry = history[i];
      const entrySegment = entry.path.split('/').filter(Boolean)[0];
      const entryConfig = getBreadcrumbConfig(entrySegment, config);
      if (entryConfig?.isRootSection) {
        rootEntry = entry;
        break;
      }
    }

    if (rootEntry && rootEntry.path !== currentPath) {
      const rootSegment = rootEntry.path.split('/').filter(Boolean)[0];
      const rootSegmentConfig = getBreadcrumbConfig(rootSegment, config);
      // Skip the root-section label when the current path is a direct child (e.g.
      // /admin/:userId): the Back button already returns there, so the label is
      // redundant — UNLESS the root opts to keep itself in the child trail.
      const isDirectChild = pathSegments.length === 2 && pathSegments[0] === rootSegment;
      const collapseRoot = isDirectChild && rootSegmentConfig?.keepRootInChildTrail !== true;

      if (rootSegment && !isHidden(rootSegment, config) && !collapseRoot) {
        const rootPath = rootSegmentConfig?.path || rootEntry.path;
        pathSoFar = rootPath;
        segmentList.push({
          label: generateLabel(rootSegment, config),
          path: pathSoFar,
          isClickable: true,
        });
      } else if (collapseRoot) {
        skippedRootSegment = rootSegment;
        // Still advance pathSoFar so child segments build correct full paths
        pathSoFar = rootSegmentConfig?.path || `/${rootSegment}`;
      }
    }
  }

  // Add segments from current path
  for (let i = 0; i < pathSegments.length; i += 1) {
    const segment = pathSegments[i];
    const segmentLabel = generateLabel(segment, config);
    const alreadyAdded = segmentList.find((s) => s.label === segmentLabel);

    // Skip the root segment in path iteration if it was intentionally omitted above
    if (segment === skippedRootSegment) {
      continue;
    }

    // Skip unresolved segments (hide until name is available)
    if (!isSegmentResolved(segment, config, entities)) {
      continue;
    }

    if (!isHidden(segment, config) && !alreadyAdded) {
      const segmentConfig = getBreadcrumbConfig(segment, config);
      if (segmentConfig?.path) {
        pathSoFar = segmentConfig.path;
      } else {
        pathSoFar += `/${segment}`;
      }
      const isCurrentPage = i === pathSegments.length - 1;

      // Use resolved name if available
      const label = entities[segment] || segmentLabel;

      segmentList.push({
        label,
        path: pathSoFar,
        isClickable: isClickable(segment, isCurrentPage, config),
      });
    }
  }

  return segmentList;
};

export const useBreadcrumbStore = create<BreadcrumbStore>()(
  devtools(
    (set, get) => ({
      entities: {},
      tenants: {},
      projects: {},
      currentPath: '',
      history: [],
      segments: [],
      config: defaultBreadcrumbConfig,
      maxHistorySize: 50,

      registerEntity: (entityId: string, entityName: string) => {
        set(
          (state) => {
            const entities = { ...state.entities, [entityId]: entityName };
            // Rebuild segments with new entity name
            const segments = buildSegments(
              state.currentPath,
              state.history,
              state.config,
              entities
            );
            return { entities, segments };
          },
          false,
          'breadcrumb/registerEntity'
        );
      },

      getEntityName: (entityId: string) => get().entities[entityId],

      // Legacy actions delegate to generic registerEntity
      registerTenant: (tenantId: string, tenantName: string) => {
        get().registerEntity(tenantId, tenantName);
        set(
          (state) => ({ tenants: { ...state.tenants, [tenantId]: tenantName } }),
          false,
          'breadcrumb/registerTenant'
        );
      },

      registerProject: (projectId: string, projectName: string) => {
        get().registerEntity(projectId, projectName);
        set(
          (state) => ({ projects: { ...state.projects, [projectId]: projectName } }),
          false,
          'breadcrumb/registerProject'
        );
      },

      getTenantName: (tenantId: string) => get().entities[tenantId],

      getProjectName: (projectId: string) => get().entities[projectId],

      setCurrentPath: (path: string) => {
        set(
          (state) => {
            // Don't add if same as last entry
            if (state.history.length > 0 && state.history[state.history.length - 1].path === path) {
              return state;
            }

            // Build label from last segment
            const pathSegments = path.split('/').filter(Boolean);
            const lastSegment = pathSegments[pathSegments.length - 1] || 'home';
            const label = generateLabel(lastSegment, state.config);

            const newEntry: NavigationHistoryEntry = {
              path,
              label,
              timestamp: Date.now(),
            };

            // Maintain max history size
            let newHistory = [...state.history, newEntry];
            if (newHistory.length > state.maxHistorySize) {
              newHistory = newHistory.slice(-state.maxHistorySize);
            }

            // Build segments
            const segments = buildSegments(path, newHistory, state.config, state.entities);

            return {
              currentPath: path,
              history: newHistory,
              segments,
            };
          },
          false,
          'breadcrumb/setCurrentPath'
        );
      },

      setConfig: (config: BreadcrumbConfig) => {
        set(
          (state) => {
            const segments = buildSegments(
              state.currentPath,
              state.history,
              config,
              state.entities
            );
            return { config, segments };
          },
          false,
          'breadcrumb/setConfig'
        );
      },

      clearHistory: () => {
        set({ history: [], segments: [], currentPath: '' }, false, 'breadcrumb/clearHistory');
      },

      clear: () => {
        set(
          {
            entities: {},
            tenants: {},
            projects: {},
            history: [],
            segments: [],
            currentPath: '',
          },
          false,
          'breadcrumb/clear'
        );
      },
    }),
    { name: 'Helios Breadcrumb Store' }
  )
);
